import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { SEOMeta } from '../components/SEOMeta';
import {
  Section,
  Container,
  SectionTitle,
  Card,
  Button,
} from "../components/ui";
// import { ComplianceKitCTA } from "../components/sections/shared";
import { RelatedPages } from '../components/RelatedPages';
import styles from "./Expertise.module.css";

export function Expertise() {
  const { t } = useTranslation('expertise');
  const { lp } = useLocalizedPath();

  const auditPoints = t('audit.points', { returnObjects: true }) as { title: string; text: string }[];
  const slaData = t('data.sla', { returnObjects: true }) as { indicator: string; engagement: string }[];
  const ctaFeatures = t('cta.features', { returnObjects: true }) as string[];
  const certItems = t('certifications.items', { returnObjects: true }) as {
    name: string; description: string; status: string; target: string;
  }[];

  return (
    <>
      <SEOMeta title={t('meta.title')} description={t('meta.description')} />

      <Section variant="dark" className={styles.hero}>
        <Container>
          <div className={styles.heroContent}>
            <h1>{t('hero.title')}</h1>
            <p className={styles.heroSubtitle}>{t('hero.subtitle')}</p>
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <div className={styles.auditSection}>
            <div className={styles.auditContent}>
              <SectionTitle title={t('audit.sectionTitle')} />
              <p className={styles.intro}>{t('audit.intro')}</p>
              <div className={styles.auditGrid}>
                {auditPoints.map((point, i) => (
                  <Card key={i}>
                    <h3 className={styles.pointTitle}>{point.title}</h3>
                    <p className={styles.pointText}>{point.text}</p>
                  </Card>
                ))}
              </div>
            </div>
            <div className={styles.auditImage}>
              <img src="/images/workshop-cables.webp" alt={t('audit.imageAlt')} width={1920} height={2697} loading="lazy" />
            </div>
          </div>
        </Container>
      </Section>

      <Section variant="gray">
        <Container>
          <div className={styles.dataSection}>
            <div className={styles.dataImage}>
              <img src="/images/api-integration.webp" alt={t('data.imageAlt')} width={1200} height={800} loading="lazy" />
            </div>
            <div className={styles.dataContent}>
              <SectionTitle title={t('data.sectionTitle')} />
              <p className={styles.intro}>{t('data.intro')}</p>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('data.tableHeaders.indicator')}</th>
                      <th>{t('data.tableHeaders.engagement')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slaData.map((row, i) => (
                      <tr key={i}>
                        <td>{row.indicator}</td>
                        <td>{row.engagement}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <SectionTitle title={t('certifications.sectionTitle')} centered />
          <p className={styles.certIntro}>{t('certifications.intro')}</p>
          <div className={styles.certGrid}>
            {certItems.map((item, i) => (
              <Card key={i} className={styles.certCard}>
                <div className={styles.certHeader}>
                  <h3 className={styles.certName}>{item.name}</h3>
                  <span className={`${styles.certStatus} ${styles[`certStatus${item.status.replace(/\s+/g, '')}`] || ''}`}>
                    {item.status}
                  </span>
                </div>
                <p className={styles.certDesc}>{item.description}</p>
                <span className={styles.certTarget}>{item.target}</span>
              </Card>
            ))}
          </div>
          <p className={styles.certDisclaimer}>{t('certifications.disclaimer')}</p>
        </Container>
      </Section>

      {/* <ComplianceKitCTA namespace="expertise" /> */}

      <Section variant="dark">
        <Container>
          <div className={styles.labProof}>
            <SectionTitle title={t('labProof.sectionTitle')} centered />
            <p className={styles.labProofText}>{t('labProof.text')}</p>
            <Button
              href="https://www.quelbonplan.fr"
              variant="secondary"
              className={styles.labProofCta}
            >
              {t('labProof.cta')}
            </Button>
            <div className={styles.laptop}>
              <div className={styles.laptopScreen}>
                <div className={styles.laptopTopbar}>
                  <span className={styles.topbarDot} />
                  <span className={styles.topbarDot} />
                  <span className={styles.topbarDot} />
                  <div className={styles.urlBar}>
                    <svg viewBox="0 0 12 12" fill="none" stroke="#8b9099" strokeWidth="1.5">
                      <rect x="1" y="1" width="10" height="10" rx="2" />
                    </svg>
                    quelbonplan.fr
                  </div>
                </div>
                <div className={styles.laptopViewport}>
                  <img src="/images/quelbonplan-catalogue.png" alt={t('labProof.imageAlt')} width={1712} height={1288} loading="lazy" />
                </div>
              </div>
              <div className={styles.laptopBase} />
              <div className={styles.laptopShadow} />
            </div>
          </div>
        </Container>
      </Section>

      <Section variant="light">
        <Container>
          <div className={styles.ctaWrapper}>
            <div className={styles.ctaContent}>
              <span className={styles.ctaBadge}>{t('cta.badge')}</span>
              <h2 className={styles.ctaTitle}>{t('cta.title')}</h2>
              <p className={styles.ctaSubtitle}>{t('cta.subtitle')}</p>
              <p className={styles.ctaMarketContext}>{t('cta.marketContext')}</p>
              <div className={styles.ctaFeatures}>
                {ctaFeatures.map((feature, i) => (
                  <div key={i} className={styles.ctaFeature}>
                    {i === 0 && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                    {i === 1 && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
                      </svg>
                    )}
                    {i === 2 && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <div className={styles.ctaActions}>
                <Button href={lp('/contact')} data-attr="cta-contact">{t('cta.ctaButton')}</Button>
                <a href="mailto:partners@qbp.solutions" className={styles.ctaSecondaryLink} data-attr="contact-email">
                  {t('cta.ctaSecondary')}
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      <RelatedPages currentFrPath="/expertise" />
    </>
  );
}
